import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Plus, Settings2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import type { Empresa } from '@/services/api/empresas';
import { LogoAvatar } from './LogoAvatar';

interface EmpresaPickerProps {
  empresaActiva: Empresa;
  empresas: Empresa[];
  /** True = el user puede crear empresas y editar la ficha (APEX, o APEX+PARTNER). */
  canEdit: boolean;
  canCreate: boolean;
  /** Variantes de layout. */
  variant: 'topbar' | 'sidebar';
  /** Solo aplica en variant='sidebar' — cuando colapsado, muestra solo el logo. */
  collapsed?: boolean;
  onEdit: () => void;
  onCreate: () => void;
}

/**
 * Selector de empresa con logo + dropdown elegante. Un solo componente
 * que se renderea distinto según `variant`:
 *  - `topbar`: compacto horizontal con ícono + kicker + nombre.
 *  - `sidebar`: más ancho, full width del sidebar, con highlight azul.
 *
 * El dropdown lista todas las empresas con logo + check en la activa,
 * y arriba del footer tiene "Editar empresa" (canEdit) y "Nueva empresa"
 * (canCreate).
 */
export function EmpresaPicker({
  empresaActiva,
  empresas,
  canEdit,
  canCreate,
  variant,
  collapsed = false,
  onEdit,
  onCreate,
}: EmpresaPickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function switchTo(e: Empresa) {
    setOpen(false);
    if (e.id !== empresaActiva.id) navigate(routes.empresa.dashboard(e.id));
  }

  const trigger =
    variant === 'topbar' ? (
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group flex items-center gap-2.5 rounded-md border border-surface-line bg-white py-1.5 pl-1.5 pr-3 transition',
          'hover:border-blue/40 hover:shadow-premium-soft',
          open && 'border-blue/50 shadow-premium-soft',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <LogoAvatar
          src={empresaActiva.logo_url}
          name={empresaActiva.nombre}
          size={36}
          shape="square"
        />
        <div className="text-left">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
            Empresa
          </p>
          <p className="text-sm font-semibold leading-tight text-neutral-ink">
            {empresaActiva.nombre}
          </p>
        </div>
        <ChevronDown
          size={14}
          className={cn(
            'ml-1 text-neutral-muted transition-transform',
            open && 'rotate-180 text-blue',
          )}
        />
      </button>
    ) : collapsed ? (
      // Sidebar colapsado — solo el logo, sin texto, tamaño contenido
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={empresaActiva.nombre}
        className={cn(
          'grid h-10 w-10 place-items-center rounded-md border border-blue-30 bg-blue-10/40 transition',
          'hover:border-blue/50 hover:bg-blue-10/70',
          open && 'border-blue/60 bg-blue-10/80',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Empresa activa: ${empresaActiva.nombre}`}
      >
        <LogoAvatar
          src={empresaActiva.logo_url}
          name={empresaActiva.nombre}
          size={28}
          shape="square"
        />
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-3 rounded-md border border-blue-30 bg-blue-10/40 p-2.5 text-left transition',
          'hover:border-blue/50 hover:bg-blue-10/70',
          open && 'border-blue/60 bg-blue-10/80',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <LogoAvatar
          src={empresaActiva.logo_url}
          name={empresaActiva.nombre}
          size={40}
          shape="square"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-blue/80">
            Empresa activa
          </p>
          <p className="truncate text-sm font-semibold text-neutral-ink">
            {empresaActiva.nombre}
          </p>
        </div>
        <ChevronDown
          size={14}
          className={cn(
            'flex-none text-blue/60 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
    );

  const otras = empresas.filter((e) => e.id !== empresaActiva.id);

  return (
    <div ref={rootRef} className="relative">
      {trigger}

      {open && (
        <div
          className={cn(
            'absolute z-50 mt-2 overflow-hidden rounded-md border border-surface-line bg-white shadow-premium',
            variant === 'topbar'
              ? 'left-0 w-80'
              : collapsed
                ? 'left-0 w-72'
                : 'left-0 right-0 w-auto',
          )}
          role="listbox"
        >
          {/* Empresa activa */}
          <div className="bg-blue-10/30 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-blue/80">
              Activa
            </p>
            <div className="mt-1 flex items-center gap-2.5">
              <LogoAvatar
                src={empresaActiva.logo_url}
                name={empresaActiva.nombre}
                size={32}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-ink">
                  {empresaActiva.nombre}
                </p>
                <p className="truncate text-[11px] text-neutral-muted">
                  CUIT {empresaActiva.cuit}
                </p>
              </div>
              <Check size={14} className="flex-none text-blue" />
            </div>
          </div>

          {/* Otras empresas */}
          {otras.length > 0 && (
            <div className="border-t border-surface-line">
              <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
                Cambiar a
              </p>
              <div className="max-h-60 overflow-y-auto pb-1">
                {otras.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => switchTo(e)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-blue-10/50"
                  >
                    <LogoAvatar src={e.logo_url} name={e.nombre} size={28} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-neutral-ink">{e.nombre}</p>
                      <p className="truncate text-[11px] text-neutral-muted">
                        CUIT {e.cuit}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Acciones */}
          {(canEdit || canCreate) && (
            <div className="border-t border-surface-line bg-surface-bg/40 p-1.5">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onEdit();
                  }}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-neutral-ink transition hover:bg-blue-10/60 hover:text-blue"
                >
                  <Settings2 size={14} className="text-blue" />
                  Editar empresa activa
                </button>
              )}
              {canCreate && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onCreate();
                  }}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-neutral-ink transition hover:bg-blue-10/60 hover:text-blue"
                >
                  <Plus size={14} className="text-blue" />
                  Nueva empresa
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
